/**
 * SkeletonLoader - Skeleton loading components for Admin Portal
 * 
 * Provides skeleton screens for better perceived performance while data loads.
 */

import React from 'react';

export const Skeleton = ({ 
  width = '100%', 
  height = '20px', 
  borderRadius = '4px',
  className = '',
  style = {}
}) => (
  <div 
    className={`admin-skeleton ${className}`}
    style={{ 
      width, 
      height, 
      borderRadius,
      ...style 
    }}
  />
);

export const StatCardSkeleton = () => (
  <div className="admin-stat-card admin-skeleton-card">
    <Skeleton width="48px" height="48px" borderRadius="12px" />
    <Skeleton width="80px" height="32px" style={{ marginTop: '12px' }} />
    <Skeleton width="100px" height="16px" style={{ marginTop: '8px' }} />
    <Skeleton width="60px" height="12px" style={{ marginTop: '8px' }} />
  </div>
);

export const StatGridSkeleton = ({ count = 4 }) => (
  <div className="admin-grid admin-grid--4">
    {Array.from({ length: count }).map((_, i) => (
      <StatCardSkeleton key={i} />
    ))}
  </div>
);

export const TableRowSkeleton = ({ columns = 5 }) => (
  <div className="admin-skeleton-table-row">
    {Array.from({ length: columns }).map((_, i) => (
      <Skeleton 
        key={i} 
        width={i === 0 ? '150px' : '80px'} 
        height="16px" 
      />
    ))}
  </div>
);

export const TableSkeleton = ({ rows = 5, columns = 5 }) => (
  <div className="admin-skeleton-table">
    <div className="admin-skeleton-table-header">
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton 
          key={i} 
          width={i === 0 ? '100px' : '60px'} 
          height="12px" 
        />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, i) => (
      <TableRowSkeleton key={i} columns={columns} />
    ))}
  </div>
);

export const CardSkeleton = ({ hasHeader = true, contentLines = 3 }) => (
  <div className="admin-card admin-skeleton-card">
    {hasHeader && (
      <div className="admin-card__header">
        <Skeleton width="150px" height="20px" />
        <Skeleton width="80px" height="32px" borderRadius="6px" />
      </div>
    )}
    <div className="admin-skeleton-card-content">
      {Array.from({ length: contentLines }).map((_, i) => (
        <Skeleton 
          key={i} 
          width={`${100 - (i * 10)}%`} 
          height="16px" 
          style={{ marginBottom: '12px' }}
        />
      ))}
    </div>
  </div>
);

export const ActivityItemSkeleton = () => (
  <div className="admin-skeleton-activity-item">
    <Skeleton width="32px" height="32px" borderRadius="8px" />
    <div className="admin-skeleton-activity-content">
      <Skeleton width="200px" height="14px" />
      <Skeleton width="120px" height="12px" style={{ marginTop: '4px' }} />
    </div>
  </div>
);

export const ActivityListSkeleton = ({ count = 5 }) => (
  <div className="admin-skeleton-activity-list">
    {Array.from({ length: count }).map((_, i) => (
      <ActivityItemSkeleton key={i} />
    ))}
  </div>
);

export const ChartSkeleton = ({ height = '200px' }) => (
  <div className="admin-skeleton-chart" style={{ height }}>
    <div className="admin-skeleton-chart-bars">
      {Array.from({ length: 7 }).map((_, i) => (
        <div 
          key={i} 
          className="admin-skeleton-chart-bar"
          style={{ height: `${30 + Math.random() * 60}%` }}
        />
      ))}
    </div>
    <div className="admin-skeleton-chart-labels">
      {Array.from({ length: 7 }).map((_, i) => (
        <Skeleton key={i} width="30px" height="10px" />
      ))}
    </div>
  </div>
);

export const DashboardSkeleton = () => (
  <div className="admin-dashboard-skeleton">
    <StatGridSkeleton count={4} />
    <div className="admin-grid admin-grid--2" style={{ marginTop: '24px' }}>
      <CardSkeleton hasHeader contentLines={5} />
      <CardSkeleton hasHeader contentLines={3} />
    </div>
  </div>
);

export const UserManagementSkeleton = () => (
  <div className="admin-user-management-skeleton">
    <div className="admin-skeleton-header">
      <Skeleton width="150px" height="28px" />
      <div className="admin-skeleton-controls">
        <Skeleton width="200px" height="40px" borderRadius="8px" />
        <Skeleton width="120px" height="40px" borderRadius="8px" />
      </div>
    </div>
    <TableSkeleton rows={8} columns={6} />
  </div>
);

export default {
  Skeleton,
  StatCardSkeleton,
  StatGridSkeleton,
  TableRowSkeleton,
  TableSkeleton,
  CardSkeleton,
  ActivityItemSkeleton,
  ActivityListSkeleton,
  ChartSkeleton,
  DashboardSkeleton,
  UserManagementSkeleton
};
